(() => {
  const root = document.getElementById('tm-cart');
  if (!root) return;

  const drawerEl = root.querySelector('.tm-cart__drawer');
  const itemsEl = document.getElementById('tm-cart-items');
  const subtotalEl = document.getElementById('tm-cart-subtotal');
  const freeEl = document.getElementById('tm-cart-free');

  // ---------------- money helpers ----------------
  const getActiveCurrency = () => {
    try {
      return (window.Shopify?.currency?.active || window.Shopify?.currency?.currency || 'EUR').toUpperCase();
    } catch {
      return 'EUR';
    }
  };

  const money = (cents) => {
    const cur = getActiveCurrency();
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur }).format((cents || 0) / 100);
    } catch {
      return ((cents || 0) / 100).toFixed(2) + (cur === 'CHF' ? ' CHF' : ' €');
    }
  };

  const freeShippingBannerText = () => {
    const cur = getActiveCurrency();
    return cur === 'CHF' ? 'KOSTENLOSER VERSAND AB 29 CHF' : 'KOSTENLOSTER VERSAND AB 29 €';
  };

  // ---------------- API ----------------
  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to load cart');
    return res.json();
  };

  const changeLine = async (key, quantity, signal) => {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
      signal,
    });
    if (!res.ok) throw new Error('Failed to change line');
    return res.json();
  };

  // ---------------- state (fixes missed clicks + jump backs) ----------------
  // key -> { desired, confirmed, seq, timer, controller }
  const lineState = new Map();
  const getState = (key) => {
    if (!lineState.has(key)) {
      lineState.set(key, { desired: null, confirmed: null, seq: 0, timer: null, controller: null });
    }
    return lineState.get(key);
  };

  const findCartItemByKey = (cart, key) => cart?.items?.find((it) => it.key === key);

  // ---------------- render (keeps your original markup/classes) ----------------
  const render = async (cart = null) => {
    const data = cart || (await fetchCart());

    // footer total (keep your existing behavior)
    if (subtotalEl) subtotalEl.textContent = money(data.items_subtotal_price);

    // Always-on banner text
    if (freeEl) freeEl.textContent = freeShippingBannerText();

    // items
    if (!itemsEl) return;
    itemsEl.innerHTML = '';

    (data.items || []).forEach((item) => {
      const st = getState(item.key);

      // While a change is in-flight, keep showing the user's latest desired qty
      const shownQty = Number.isFinite(st.desired) ? st.desired : item.quantity;
      st.confirmed = item.quantity;

      const li = document.createElement('li');
      li.className = 'tm-cart__item';
      li.dataset.key = item.key;

      li.innerHTML = `
        <img class="tm-cart__img" src="${item.image || ''}" alt="${item.product_title || ''}">
        <div>
          <div class="tm-cart__top">
            <div>
              <p class="tm-cart__name">${item.product_title || ''}</p>
            </div>
            <div class="tm-cart__price">${money(item.final_line_price)}</div>
          </div>

          <div class="tm-cart__qtyrow">
            <div class="tm-cart__qty">
              <button type="button" data-dec aria-label="Menge verringern">–</button>
              <input type="text" inputmode="numeric" value="${shownQty}" aria-label="Menge" data-qty>
              <button type="button" data-inc aria-label="Menge erhöhen">+</button>
            </div>

            <button type="button" class="tm-cart__remove" data-remove aria-label="Entfernen">
              <svg class="tm-cart__trash" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 3h6l1 2h4v2H4V5h4l1-2Zm1 7h2v9h-2v-9Zm4 0h2v9h-2v-9ZM7 10h2v9H7v-9Zm-1 12h12a2 2 0 0 0 2-2V7H4v13a2 2 0 0 0 2 2Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      `;

      itemsEl.appendChild(li);
    });
  };

  const setDesiredInDom = (key, nextQty) => {
    const li = itemsEl?.querySelector(`[data-key="${CSS.escape(key)}"]`);
    if (!li) return;
    const inp = li.querySelector('[data-qty]');
    if (inp) inp.value = String(nextQty);
  };

  const scheduleCommit = (key) => {
    const st = getState(key);
    if (st.timer) window.clearTimeout(st.timer);
    st.timer = window.setTimeout(() => commit(key), 120);
  };

  const commit = async (key) => {
    const st = getState(key);
    const desired = Number.isFinite(st.desired) ? st.desired : st.confirmed;

    // cancel any in-flight request for this line and send only the latest
    if (st.controller) st.controller.abort();
    st.controller = new AbortController();
    const seq = ++st.seq;

    try {
      const cart = await changeLine(key, desired, st.controller.signal);

      // Ignore stale responses
      if (seq !== st.seq) return;

      const updated = findCartItemByKey(cart, key);
      st.confirmed = updated ? updated.quantity : 0;
      st.desired = null;

      await render(cart);
    } catch (e) {
      if (e?.name === 'AbortError') return;

      // On real error, refresh cart and reset optimistic value
      try {
        const cart = await fetchCart();
        const updated = findCartItemByKey(cart, key);
        st.confirmed = updated ? updated.quantity : 0;
        st.desired = null;
        await render(cart);
      } catch {
        // keep UI as-is
      }
    }
  };

  // ---------------- qty handlers (event delegation, no missed clicks) ----------------
  itemsEl?.addEventListener('click', (e) => {
    const inc = e.target.closest('[data-inc]');
    const dec = e.target.closest('[data-dec]');
    const rm = e.target.closest('[data-remove]');
    if (!inc && !dec && !rm) return;

    const li = e.target.closest('[data-key]');
    if (!li) return;

    const key = li.dataset.key;
    const st = getState(key);

    const inp = li.querySelector('[data-qty]');
    const curFromDom = inp ? parseInt(inp.value, 10) : NaN;
    const cur = Number.isFinite(st.desired)
      ? st.desired
      : Number.isFinite(curFromDom)
        ? curFromDom
        : (st.confirmed ?? 1);

    let next = cur;
    if (inc) next = cur + 1;
    if (dec) next = Math.max(0, cur - 1);
    if (rm) next = 0;

    st.desired = next;
    setDesiredInDom(key, next);
    scheduleCommit(key);
  });

  itemsEl?.addEventListener('change', (e) => {
    const inp = e.target.closest('[data-qty]');
    if (!inp) return;

    const li = inp.closest('[data-key]');
    if (!li) return;

    const key = li.dataset.key;
    const st = getState(key);

    const val = parseInt(inp.value, 10);
    const next = Number.isFinite(val) ? Math.max(0, val) : (st.confirmed ?? 1);

    st.desired = next;
    setDesiredInDom(key, next);
    scheduleCommit(key);
  });


const open = async () => {
  root.setAttribute('aria-hidden', 'false');
  root.classList.add('is-open');
  document.documentElement.classList.add('tm-cart-open');
  document.body.classList.add('tm-cart-open');
  await render();
};

const close = () => {
  root.setAttribute('aria-hidden', 'true');
  root.classList.remove('is-open');
  document.documentElement.classList.remove('tm-cart-open');
  document.body.classList.remove('tm-cart-open');
};
  // click outside drawer closes (covers overlay + any outside click)
  root.addEventListener(
    'mousedown',
    (e) => {
      if (root.getAttribute('aria-hidden') !== 'false') return;
      if (!drawerEl) return;
      if (drawerEl.contains(e.target)) return;
      close();
    },
    true
  );

  // escape to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.getAttribute('aria-hidden') === 'false') close();
  });

  // open / close triggers (header + overlay)
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-tm-cart-open]');
    if (openBtn) {
      e.preventDefault();
      open();
      return;
    }

    const closeBtn = e.target.closest('[data-tm-cart-close]');
    if (closeBtn) {
      e.preventDefault();
      close();
    }
  });

  // --------- expose API ----------
  window.TMCart = window.TMCart || {};
  window.TMCart.open = open;
  window.TMCart.close = close;
  window.TMCart.setCart = (cart) => render(cart);
  window.TMCart.refresh = async () => render(await fetchCart());

  // --------- allow external updates ----------
  window.addEventListener('tm:cart:updated', (e) => {
    if (e?.detail) window.TMCart.setCart(e.detail);
    else window.TMCart.refresh();
  });
})();
